import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { FundsSummary } from "@/utils/accounting";
import type { Expense, FundKey } from "@/types";
import NumberInput from "@/components/base/NumberInput";
import { formatCOP } from "@/utils/format";

interface PayExpenseModalProps {
  open: boolean;
  expense: Expense | null;
  funds: FundsSummary;
  onClose: () => void;
  onPayFromFund: (id: string, fund: FundKey, amount: number) => void;
  onPayWithLoan: (id: string, amount: number) => void;
}

// Paga (o abona) una compra / gasto pendiente desde un fondo o recargando con
// un préstamo de Juank.unlimited.
const PayExpenseModal = ({
  open,
  expense,
  funds,
  onClose,
  onPayFromFund,
  onPayWithLoan,
}: PayExpenseModalProps) => {
  const [amount, setAmount] = useState(0);
  const [fund, setFund] = useState<FundKey>("caja");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !expense) return;
    setAmount(Math.max(0, expense.amount - expense.paidAmount));
    setFund("caja");
    setError("");
  }, [open, expense]);

  if (!open || !expense) return null;

  const remaining = Math.max(0, expense.amount - expense.paidAmount);
  const selected = funds.funds.find((f) => f.key === fund);
  const value = Math.max(0, Math.min(Number(amount) || 0, remaining));
  const insufficient = value > (selected?.balance ?? 0);

  const payFund = () => {
    if (value <= 0) {
      setError("El monto debe ser mayor a cero.");
      return;
    }
    onPayFromFund(expense.id, fund, value);
  };

  const payLoan = () => {
    if (value <= 0) {
      setError("El monto debe ser mayor a cero.");
      return;
    }
    onPayWithLoan(expense.id, value);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pagar compra / gasto"
      icon="ri-secure-payment-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cerrar
          </button>
          <button onClick={payFund} className={primaryButtonClass}>
            <i className="ri-safe-2-line" /> Pagar con {selected?.short ?? "fondo"}
          </button>
        </>
      }
    >
      <div className="rounded-lg border border-background-300/40 bg-background-200/30 p-3 mb-4">
        <p className="text-sm font-semibold text-foreground-950">{expense.concept}</p>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">Total</p>
            <p className="text-sm font-bold text-foreground-950">{formatCOP(expense.amount)}</p>
          </div>
          <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">Pagado</p>
            <p className="text-sm font-bold text-foreground-950">{formatCOP(expense.paidAmount)}</p>
          </div>
          <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2">
            <p className="text-[10px] text-accent-300">Saldo</p>
            <p className="text-sm font-bold text-accent-200">{formatCOP(remaining)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Monto a pagar (COP)</label>
          <NumberInput min={0} value={amount} onChange={setAmount} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Pagar desde el fondo</label>
          <select
            value={fund}
            onChange={(e) => setFund(e.target.value as FundKey)}
            className={inputClass}
          >
            {funds.funds.map((f) => (
              <option key={f.key} value={f.key} className="bg-background-100">
                {f.short} · saldo {formatCOP(f.balance)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {insufficient && (
        <div className="mt-3 rounded-md bg-secondary-500/10 border border-secondary-500/30 px-3 py-2 flex items-start gap-2">
          <i className="ri-error-warning-line text-secondary-200 mt-0.5" />
          <p className="text-[11px] text-foreground-700">
            El fondo <span className="font-semibold text-foreground-950">{selected?.short}</span> no
            alcanza. Usa{" "}
            <span className="font-semibold text-foreground-950">“Pagar con Préstamo de Juank”</span>{" "}
            para recargar la Caja Menor y cubrir el pago.
          </p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-background-300/25">
        <button
          onClick={payLoan}
          className="w-full px-4 py-2.5 rounded-md border border-secondary-500/40 text-secondary-100 hover:bg-secondary-500/10 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
        >
          <i className="ri-exchange-dollar-line" /> Pagar con Préstamo de Juank (
          {formatCOP(value)})
        </button>
        <p className="text-[10px] text-foreground-500 mt-2 text-center">
          Se inyecta el dinero como liquidez a la Caja Menor y queda como deuda{" "}
          <span className="font-semibold text-foreground-700">“Por devolver a Juank”</span>.
        </p>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default PayExpenseModal;